'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { Icon } from '@ui/icons';
import { Modal } from '@web/components/Modal';
import { VideoModal } from '@web/components/VideoModal';
import { useReporter } from '../../../reporter';
import { useShopifyOfferContext } from '../../../offer/shopifyContext';
import { rotatingPromoBarClicked } from './events';
import {
  StyledRotatingPromoBar,
  MessageWrapper,
  PromoIcon,
  PromoLink,
  PromoLinkButton,
  PromoViewport,
  ArrowButton,
} from './styles';

const ROTATION_INTERVAL_MS = 6000;

export const RotatingPromoBar = ({
  messages = [],
  backgroundColor = 'background.dark',
  fontColor = 'text.lightest',
  sticky = false,
  enableRotation = false,
}) => {
  const [slideState, setSlideState] = useState({
    activeIndex: 0,
    outgoingIndex: null,
    direction: 'next',
  });
  const [isPaused, setIsPaused] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { formattedAmount } = useShopifyOfferContext();
  const reporter = useReporter();
  const animGenRef = useRef(0);

  const navigate = useCallback(
    dir => {
      if (messages.length <= 1) return;
      const currentGen = ++animGenRef.current;
      setSlideState(prev => {
        const count = messages.length;
        const nextIndex =
          dir === 'next'
            ? (prev.activeIndex + 1) % count
            : (prev.activeIndex - 1 + count) % count;
        return {
          activeIndex: nextIndex,
          outgoingIndex: prev.activeIndex,
          direction: dir,
        };
      });
      setTimeout(() => {
        if (animGenRef.current === currentGen) {
          setSlideState(prev => ({ ...prev, outgoingIndex: null }));
        }
      }, 500);
    },
    [messages.length],
  );

  const advance = useCallback(() => navigate('next'), [navigate]);
  const goToPrev = () => navigate('prev');
  const goToNext = () => navigate('next');

  useEffect(() => {
    if (!enableRotation || messages.length <= 1 || isPaused) return;
    const timer = setInterval(advance, ROTATION_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enableRotation, messages.length, isPaused, advance]);

  // Reset modal state when the active message changes.
  useEffect(() => {
    setShowModal(false);
  }, [slideState.activeIndex]);

  if (!messages.length) return null;

  const { activeIndex, outgoingIndex, direction } = slideState;

  const {
    linkText,
    linkTarget,
    triggerModal = false,
    modal,
  } = messages[activeIndex];
  const interpolatedLinkText = linkText
    ? interpolate(linkText, { offer: formattedAmount })
    : null;

  const handleLinkClick = () => {
    reporter.tag(rotatingPromoBarClicked(interpolatedLinkText, linkTarget));
    if (triggerModal) setShowModal(true);
  };

  const modalNode =
    showModal && modal ? (
      modal.component === 'video_modal' ? (
        <VideoModal shown onClickClose={() => setShowModal(false)} {...modal} />
      ) : (
        <Modal shown onCloseModal={() => setShowModal(false)} {...modal} />
      )
    ) : null;

  const renderSlide = (msgIndex, isActive, wrapperProps) => {
    const msg = messages[msgIndex];
    const {
      message: m,
      icon: ic,
      iconAlignment: ia = 'left',
      linkText: lt,
      linkTarget: ltar,
      linkStyle: ls = 'link',
      triggerModal: tm = false,
    } = msg;
    const interp = lt ? interpolate(lt, { offer: formattedAmount }) : null;
    const icNode = ic ? (
      <PromoIcon $src={ic.src} role="img" aria-label={ic.alt} />
    ) : null;
    const onLinkClick = isActive ? handleLinkClick : () => {};
    const lnk = interp ? (
      tm ? (
        <PromoLinkButton
          $isButton={ls === 'button'}
          $fontColor={fontColor}
          onClick={onLinkClick}
        >
          {interp}
        </PromoLinkButton>
      ) : (
        <PromoLink
          $isButton={ls === 'button'}
          $fontColor={fontColor}
          href={ltar}
          onClick={onLinkClick}
        >
          {interp}
        </PromoLink>
      )
    ) : null;
    return (
      <MessageWrapper {...wrapperProps}>
        {ia === 'left' && icNode}
        {m}
        {ia === 'between' && icNode}
        {lnk}
        {ia === 'right' && icNode}
      </MessageWrapper>
    );
  };

  return (
    <StyledRotatingPromoBar
      data-testid="rotating-promo-bar"
      $backgroundColor={backgroundColor}
      $fontColor={fontColor}
      $sticky={sticky}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {messages.length > 1 && (
        <ArrowButton
          $direction="prev"
          onClick={goToPrev}
          aria-label="Previous message"
        >
          <Icon
            iconName="arrow-left"
            alt=""
            size="22px"
            color={fontColor}
            id="promo-bar-prev-arrow"
          />
        </ArrowButton>
      )}
      <PromoViewport>
        {outgoingIndex !== null ? (
          <>
            {renderSlide(activeIndex, true, {
              key: `in-${activeIndex}`,
              $entering: true,
              $direction: direction,
            })}
            {renderSlide(outgoingIndex, false, {
              key: `out-${outgoingIndex}`,
              $exiting: true,
              $direction: direction,
            })}
          </>
        ) : (
          renderSlide(activeIndex, true, { key: `static-${activeIndex}` })
        )}
      </PromoViewport>
      {messages.length > 1 && (
        <ArrowButton
          $direction="next"
          onClick={goToNext}
          aria-label="Next message"
        >
          <Icon
            iconName="arrow-forward"
            alt=""
            size="22px"
            color={fontColor}
            id="promo-bar-next-arrow"
          />
        </ArrowButton>
      )}
      {modalNode}
    </StyledRotatingPromoBar>
  );
};

RotatingPromoBar.displayName = 'RotatingPromoBar';

RotatingPromoBar.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.node,
      icon: PropTypes.shape({ src: PropTypes.string, alt: PropTypes.string }),
      iconAlignment: PropTypes.oneOf(['left', 'between', 'right']),
      linkText: PropTypes.string,
      linkTarget: PropTypes.string,
      linkStyle: PropTypes.oneOf(['link', 'button']),
      triggerModal: PropTypes.bool,
      modal: PropTypes.object,
    }),
  ),
  backgroundColor: PropTypes.string,
  fontColor: PropTypes.string,
  sticky: PropTypes.bool,
  enableRotation: PropTypes.bool,
};
